import React, { useState, useEffect } from 'react';
import { CurrencyCircleDollar, CreditCard, DeviceMobile, Calendar, Gear, SpinnerGap } from '@phosphor-icons/react';
import { SettingsLine } from './SettingsLine';
import { capitalizeFirstLetter, formatDate } from '../../../shared/utils/format';
import { fetchProducts, products } from '../../../shared/services/revenueCatService';
import type { SubscriptionPlatformInfo } from '../../../shared/types/subscription';

interface SubscriptionSectionProps {
    subscription: SubscriptionPlatformInfo | null;
    onManageSubscription: () => void;
    onViewPlans: () => void;
}

export function SubscriptionSection({ 
    subscription, 
    onManageSubscription,
    onViewPlans 
}: SubscriptionSectionProps) {
    if (!subscription) {
        return (
            <div>
                <h3 className="text-xs font-semibold uppercase tracking-wider text-[#361e06] mb-4">Subscription</h3>
                <div className="text-center py-6">
                    <p className="text-sm text-gray-500 mb-4">
                        You don't have an active subscription.
                    </p>
                    <button
                        onClick={onViewPlans}
                        className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                        View Plans
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div>
            <h3 className="text-xs font-semibold uppercase tracking-wider text-[#361e06] mb-4">Subscription</h3>
            
            <div>
                <SettingsLine 
                    label="Current Plan"
                    icon={CurrencyCircleDollar}
                >
                    <span className="text-sm text-gray-900">
                        {subscription.productDetails ? (
                            <>{subscription.productDetails.title} - {subscription.productDetails.price}/{subscription.productDetails.period}</>
                        ) : (
                            'Unknown Plan'
                        )}
                    </span>
                </SettingsLine>

                <SettingsLine 
                    label="Status"
                    icon={CreditCard}
                >
                    <span className="text-sm text-gray-900">
                        {capitalizeFirstLetter(subscription.status)}
                        {subscription.status === 'in_grace_period' && (
                            <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                Grace Period
                            </span>
                        )}
                    </span>
                </SettingsLine>

                <SettingsLine 
                    label="Platform"
                    icon={DeviceMobile}
                >
                    <span className="text-sm text-gray-900">
                        {capitalizeFirstLetter(subscription.platform)}
                    </span>
                </SettingsLine>

                <SettingsLine 
                    label="Expires"
                    icon={Calendar}
                >
                    <span className="text-sm text-gray-900">
                        {formatDate(subscription.expiresAt)}
                    </span>
                </SettingsLine>

                <div className="mt-6">
                    {subscription.platform === 'web' ? (
                        <button
                            onClick={onManageSubscription}
                            className="text-sm font-medium text-blue-600 hover:text-blue-500"
                        >
                            Manage your subscription
                        </button>
                    ) : (
                        <p className="text-sm text-gray-500">
                            Use the Theos mobile app to manage your subscription
                        </p>
                    )}
                </div>
            </div>
        </div>
    );
}